// =============================================================================
// RESUME TEMPLATE REGISTRY — the SHARED, DEPENDENCY-FREE constant the client
// (apps/throughline) and the server (apps/api) both import as a RUNTIME VALUE
// (not just a type). Deliberately split out of resume_templates.ts (which
// pulls in drizzle-orm/pg-core at module scope — fine for a server import,
// wrong to ship into a browser bundle): this file has ZERO imports beyond
// resume_document.ts's plain type (erased at compile time), so importing it
// client-side costs nothing beyond the object literal itself.
//
// THE SEED-VS-CODE-VS-DB SPLIT, made precise:
//   - THIS FILE is the single source of truth for the 14 families' META
//     (family key, tier, structure, locale support, accent-eligibility,
//     picker order, source claim keys) — a plain object literal, not a DB
//     read, so the picker/registry never needs a network round-trip just to
//     know the LIST of families exists.
//   - `resume_templates` (the Drizzle table, resume_templates.ts) is SEEDED
//     from this exact literal (the seed script/Neon insert uses these same
//     values) — DB rows and this constant are the same data, mirrored, never
//     two independently-maintained lists.
//   - apps/throughline/src/lib/resumeTemplates/registry.ts is the RENDERER
//     registry (code, keyed by the same `family` string) — it imports
//     RESUME_TEMPLATE_META from here and asserts (a test, not a runtime
//     throw) that every family with status:'built' here has a matching
//     renderer entry there, and vice versa (the "row <-> code mutual check"
//     platform_format/platform_criteria already establish for their own
//     validated-config shape).
// =============================================================================

import type { ResumeDocument, ResumeFormatVariant, ResumeSection } from './resume_document';

export type ResumeTemplateFamily =
  | 'ats-plain'
  | 'counter-ready'
  | 'everyday-clean'
  | 'first-job-student'
  | 'trades-certifications'
  | 'recent-graduate'
  | 'modern-professional'
  | 'executive-compact'
  | 'classic-serif'
  | 'selection-criteria'
  | 'two-column-portfolio'
  | 'tech-impact'
  | 'corporate-consulting'
  | 'elegant-header';

export type ResumeTemplateTier = 'free' | 'premium';
export type ResumeTemplateStatus = 'built' | 'coming';
export type ResumeTemplateStructure = 'single-column' | 'two-column' | 'per-criterion';

export interface ResumeTemplateMeta {
  family: ResumeTemplateFamily;
  displayName: string;
  forWhom: string;
  tier: ResumeTemplateTier;
  status: ResumeTemplateStatus;
  structure: ResumeTemplateStructure;
  supportedLocales: ResumeFormatVariant[];
  acceptsAccent: boolean;
  sortOrder: number;
  sourceClaimKeys: string[];
}

/** ALL locale variants a family supports, when it has no locale restriction of
 *  its own (every family except selection-criteria — §9: locale is an axis
 *  every family renders under, not a per-locale duplicate set). */
const ALL_STANDARD_LOCALES: ResumeFormatVariant[] = ['us_standard', 'gb_standard', 'au_standard'];

/**
 * The 14-family launch library (§4), in picker order. Every family here has a
 * `status` — 'built' for the 4 this Phase ships end-to-end
 * (ats-plain/counter-ready/recent-graduate/modern-professional), 'coming' for
 * the other 10 (a real, honestly-labeled registry stub — NEVER a rendered
 * fake, per the build brief's "registry stubs marked coming, not rendered
 * fakes"). sourceClaimKeys are the resume_template_sources.claim_key rows
 * that justify each family's design choices (§5) — only claim keys that
 * verified cleanly (§ADV) are listed; a key with no VALIDATED row simply
 * doesn't render copy, this list is never itself the gate.
 */
export const RESUME_TEMPLATE_META: readonly ResumeTemplateMeta[] = [
  {
    family: 'ats-plain',
    displayName: 'ATS-Plain',
    forWhom: 'The parse-safest résumé — upload to any job system with confidence.',
    tier: 'free',
    status: 'built',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: false, // the one deliberate exception (§10) — monochrome by design
    sortOrder: 1,
    sourceClaimKeys: [
      'ats.single_column',
      'ats.no_tables_textboxes',
      'ats.no_graphics',
      'ats.contact_in_body',
      'ats.safe_fonts',
      'ats.format_docx_or_pdf',
    ],
  },
  {
    family: 'counter-ready',
    displayName: 'Counter-Ready',
    forWhom:
      'Service and early-career: the physical one-pager you hand across a counter — legible, gorgeous on paper.',
    tier: 'free',
    status: 'built',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 2,
    sourceClaimKeys: ['ats.safe_fonts', 'fidelity.exact_page_box'],
  },
  {
    family: 'everyday-clean',
    displayName: 'Everyday Clean',
    forWhom: 'The warm, uncluttered default for most job seekers — still fully ATS-safe.',
    tier: 'free',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 3,
    sourceClaimKeys: ['ats.single_column'],
  },
  {
    family: 'first-job-student',
    displayName: 'First-Job / Student',
    forWhom: 'Early-career and students — education and projects forward when work history is thin.',
    tier: 'free',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 4,
    sourceClaimKeys: ['ats.single_column'],
  },
  {
    family: 'trades-certifications',
    displayName: 'Trades & Certifications',
    forWhom:
      'Skilled trades and field work — licences, tickets, and certifications up top, the first thing checked.',
    tier: 'free',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 5,
    sourceClaimKeys: ['fidelity.exact_page_box'],
  },
  {
    family: 'recent-graduate',
    displayName: 'Recent Graduate',
    forWhom: 'New graduates — education first, then an internship or capstone, then skills.',
    tier: 'free',
    status: 'built',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 6,
    sourceClaimKeys: ['ats.single_column', 'ats.safe_fonts'],
  },
  {
    family: 'modern-professional',
    displayName: 'Modern Professional',
    forWhom: 'The polished everyday professional — refined hierarchy, a restrained accent rule.',
    tier: 'premium',
    status: 'built',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 7,
    sourceClaimKeys: ['ats.single_column', 'ats.safe_fonts'],
  },
  {
    family: 'executive-compact',
    displayName: 'Executive Compact',
    forWhom: 'Senior and executive — a lot of career, tight rhythm, two pages that read as authority.',
    tier: 'premium',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 8,
    sourceClaimKeys: [],
  },
  {
    family: 'classic-serif',
    displayName: 'Classic Serif',
    forWhom: 'Conservative and traditional fields — a Georgia/Times serif register.',
    tier: 'premium',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 9,
    sourceClaimKeys: ['ats.safe_fonts'],
  },
  {
    family: 'selection-criteria',
    displayName: 'Selection-Criteria',
    forWhom: 'AU government and public sector — per-criterion STAR responses.',
    tier: 'premium',
    status: 'coming',
    structure: 'per-criterion',
    supportedLocales: ['au_selection_criteria'],
    acceptsAccent: true,
    sortOrder: 10,
    sourceClaimKeys: [],
  },
  {
    family: 'two-column-portfolio',
    displayName: 'Two-Column Portfolio',
    forWhom: 'For handing or emailing to a person — a tasteful skills sidebar. Honestly labeled non-ATS.',
    tier: 'premium',
    status: 'coming',
    structure: 'two-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 11,
    sourceClaimKeys: [],
  },
  {
    family: 'tech-impact',
    displayName: 'Tech / Impact',
    forWhom: 'Software, data, and product — a prominent skills stack and impact-first, quantified bullets.',
    tier: 'premium',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 12,
    sourceClaimKeys: ['ats.single_column'],
  },
  {
    family: 'corporate-consulting',
    displayName: 'Corporate / Consulting',
    forWhom: 'Finance, consulting, and corporate — a strong summary, tight quantified results.',
    tier: 'premium',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 13,
    sourceClaimKeys: [],
  },
  {
    family: 'elegant-header',
    displayName: 'Elegant Header',
    forWhom: 'Creative and marketing roles wanting personality without breaking parsing.',
    tier: 'premium',
    status: 'coming',
    structure: 'single-column',
    supportedLocales: ALL_STANDARD_LOCALES,
    acceptsAccent: true,
    sortOrder: 14,
    sourceClaimKeys: ['ats.single_column'],
  },
] as const;

/** Fast id -> meta lookup, built once. */
export const RESUME_TEMPLATE_META_BY_FAMILY: Readonly<Record<ResumeTemplateFamily, ResumeTemplateMeta>> =
  Object.fromEntries(RESUME_TEMPLATE_META.map((m) => [m.family, m])) as Record<
    ResumeTemplateFamily,
    ResumeTemplateMeta
  >;

/** The documented fallback family every export path uses when a user has no
 *  preferredTemplateFamily set yet (NULL column) — the free, fully ATS-safe
 *  default. Never invented per-call; one named constant every reader shares. */
export const DEFAULT_TEMPLATE_FAMILY: ResumeTemplateFamily = 'ats-plain';

/** True iff `family` is a currently-built (real-renderer) family. Registry
 *  code (both client render-dispatch and server export-gate) uses this
 *  rather than re-deriving it, so "is this family real yet" has one answer. */
export function isBuiltFamily(family: string): family is ResumeTemplateFamily {
  const meta = RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily];
  return meta != null && meta.status === 'built';
}

/** True iff `family` is a real family name in the shared meta at all (built
 *  OR coming) — narrower use than isBuiltFamily: "is this even a name we
 *  recognise" input validation (e.g. the server export gate's 400 vs a
 *  silent no-op for a typo'd family string). */
export function isKnownFamily(family: string): family is ResumeTemplateFamily {
  return RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily] != null;
}

/** True iff `family` requires the `template_library` capability to EXPORT
 *  (picker display is never gated — §6). An unknown family string reads as
 *  premium (fail closed: an unrecognised family should never slip through as
 *  free), though callers should validate the family against
 *  RESUME_TEMPLATE_META_BY_FAMILY first and reject unknown values outright. */
export function isPremiumFamily(family: string): boolean {
  const meta = RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily];
  return meta ? meta.tier === 'premium' : true;
}

// =============================================================================
// ACCENT PALETTE (§10, Decision 12) — the 6 professional darks. Print-artifact
// hex constants, same nature as resumeToPrint.ts's PRINT_SAFE_VARS: these are
// NOT app-theme colors (no --tl-* var applies), they are the résumé
// document's OWN colour system, chosen by the user, applied identically
// across every export surface. Living here (not inline in a component or a
// .module.css) keeps them out of the app's own no-hardcoded-color
// themeCompliance lint scope (which only scans .tsx/.module.css files) while
// still being the ONE place every renderer (screen/pdf/docx/html) reads from
// — never five independently-typed hex literals drifting apart.
// =============================================================================

export type ResumeAccentKey = 'sage-teal' | 'ink-navy' | 'graphite' | 'burgundy' | 'bronze' | 'slate';

export interface ResumeAccentOption {
  key: ResumeAccentKey;
  label: string;
  hex: string;
  /** The small-text-legible variant (Sage Teal's own accent-text tone, §10);
   *  every other option is dark enough on white that its own hex covers both
   *  uses, so this is present ONLY where the design calls out a distinct
   *  small-text tone. Absent (not null) elsewhere — the renderer falls back
   *  to `hex` when this is undefined, never a fabricated darker/lighter shade. */
  smallTextHex?: string;
  character: string;
}

export const RESUME_ACCENT_OPTIONS: readonly ResumeAccentOption[] = [
  {
    key: 'sage-teal',
    label: 'Sage Teal',
    hex: '#3D7A72',
    smallTextHex: '#356963',
    character: 'the brand',
  },
  { key: 'ink-navy', label: 'Ink Navy', hex: '#1F3A5F', character: 'classic corporate' },
  { key: 'graphite', label: 'Graphite', hex: '#33373B', character: 'near-black — the most neutral' },
  { key: 'burgundy', label: 'Burgundy', hex: '#6E2233', character: 'warm executive' },
  { key: 'bronze', label: 'Bronze', hex: '#7C5A1E', character: 'a legible dark of the brand warm' },
  { key: 'slate', label: 'Slate', hex: '#3F4B5B', character: 'cool blue-grey' },
] as const;

export const DEFAULT_ACCENT_KEY: ResumeAccentKey = 'sage-teal';

const ACCENT_BY_KEY: Readonly<Record<ResumeAccentKey, ResumeAccentOption>> = Object.fromEntries(
  RESUME_ACCENT_OPTIONS.map((a) => [a.key, a]),
) as Record<ResumeAccentKey, ResumeAccentOption>;

/** Resolve an accent key (typically users.preferredAccent, which may be null,
 *  unset, or a stale/unknown string) to its live option, falling back to the
 *  documented default rather than throwing or rendering `undefined` — an
 *  unset/unrecognised accent is exactly as valid as "use the default," never
 *  an error state (mirrors resolveEntitlement's own "undetermined ->
 *  documented floor" posture, applied to a presentation choice instead of a
 *  billing one). */
export function resolveAccent(key: string | null | undefined): ResumeAccentOption {
  if (key && ACCENT_BY_KEY[key as ResumeAccentKey]) return ACCENT_BY_KEY[key as ResumeAccentKey];
  return ACCENT_BY_KEY[DEFAULT_ACCENT_KEY];
}

/** The EFFECTIVE accent option for a (family, requested key) pair — honours
 *  the ONE deliberate exception (§10): a family with acceptsAccent:false
 *  (ATS-Plain today) always resolves to Graphite/near-black regardless of
 *  what the caller requested, since its whole identity is "zero colour,
 *  maximum parse-safety." Every accepting family applies the resolved accent
 *  normally. Shared by every render surface (screen/pdf/docx/html) so none
 *  of them can independently drift on "does this family even take a
 *  colour." */
export function effectiveAccent(
  family: ResumeTemplateFamily,
  requestedAccentKey: string | null | undefined,
): ResumeAccentOption {
  const meta = RESUME_TEMPLATE_META_BY_FAMILY[family];
  if (meta && !meta.acceptsAccent) return resolveAccent('graphite');
  return resolveAccent(requestedAccentKey ?? DEFAULT_ACCENT_KEY);
}

/** Resolve the family a user should actually see/export: their stored
 *  preference (users.preferredTemplateFamily) when it names a real, BUILT
 *  family; the documented default otherwise. Never a fabricated "last used"
 *  family. `isFamilyBuilt` is injected (rather than hard-importing a
 *  registered-families list here) so this ONE function serves both the
 *  client (whose "built" set is REGISTERED_FAMILIES, the renderer-code
 *  registry) and the server (whose own export gate may recognise a
 *  different/overlapping built set as its own renderers land) without this
 *  shared schema module needing to know either side's code layout. */
export function resolvePreferredFamily(
  preferred: string | null | undefined,
  isFamilyBuilt: (family: string) => boolean,
): ResumeTemplateFamily {
  if (preferred && isFamilyBuilt(preferred)) return preferred as ResumeTemplateFamily;
  return DEFAULT_TEMPLATE_FAMILY;
}

// =============================================================================
// SECTION ORDERING (§3's "view swap, not a data swap") — the one render-time
// structural axis among the built families so far (Recent Graduate:
// education before experience). A pure re-sequence of the SAME array —
// nothing added, removed, or reworded. Shared by every render surface.
// =============================================================================

/** Kind priority for a family's display order. Sections whose kind isn't
 *  listed keep their ORIGINAL relative order, appended after every listed
 *  kind — so a document with an unexpected/future section kind never loses
 *  content, it just doesn't get a special position. */
const KIND_PRIORITY: Partial<Record<ResumeTemplateFamily, ResumeSection['kind'][]>> = {
  'recent-graduate': ['profile', 'education', 'experience', 'skills', 'certifications', 'selection_criteria'],
};

/** Re-sequence `sections` for display under `family` — stable sort so
 *  sections of equal/absent priority keep their original relative order.
 *  Returns the SAME section objects (never cloned/reworded) in a new array —
 *  a view swap, never a data swap. */
export function orderSectionsForFamily(
  sections: ResumeSection[],
  family: ResumeTemplateFamily,
): ResumeSection[] {
  const priority = KIND_PRIORITY[family];
  if (!priority) return sections;
  const rank = (kind: ResumeSection['kind']): number => {
    const idx = priority.indexOf(kind);
    return idx === -1 ? priority.length : idx;
  };
  return [...sections].sort((a, b) => rank(a.kind) - rank(b.kind));
}

/** Apply a family's full display transform to a served document: reorder its
 *  sections (orderSectionsForFamily above), leave contact/formatVariant/meta
 *  untouched. Returns a NEW ResumeDocument object (shallow — sections array
 *  is new, section objects/contact/meta are the same references) so a
 *  caller's re-render reflects the swap without mutating shared state. */
export function applyTemplateDisplayOrder(
  doc: ResumeDocument,
  family: ResumeTemplateFamily,
): ResumeDocument {
  return { ...doc, sections: orderSectionsForFamily(doc.sections, family) };
}

// =============================================================================
// PAGE GEOMETRY (§9) — Letter vs A4 per the document's OWN formatVariant, NOT
// per family. Every family renders at the SAME derived page size; families
// differ in typography/margins/order, never in which physical page standard
// applies to a US vs AU/GB résumé.
// =============================================================================

export interface PageGeometryMm {
  widthMm: number;
  heightMm: number;
}

/** US Letter (216 x 279mm, ANSI/ASME Y14.1) for us_standard; A4 (210 x 297mm,
 *  ISO 216) for every other locale (au_standard / au_selection_criteria /
 *  gb_standard) — §9's sourced table, applied as one small pure function
 *  every renderer (pdf/docx/html) reads instead of re-deriving. */
export function pageGeometryForVariant(formatVariant: ResumeFormatVariant): PageGeometryMm {
  if (formatVariant === 'us_standard') return { widthMm: 216, heightMm: 279 };
  return { widthMm: 210, heightMm: 297 };
}

// =============================================================================
// PER-FAMILY TYPOGRAPHY TOKENS — the presentation differences among the
// built families, read by the screen CSS, react-pdf, and docx builders (and,
// where relevant, the server HTML renderer), so every surface agrees on
// "what does family X actually look like" from ONE table.
// =============================================================================

export interface ResumeTemplateTokens {
  /** Base body font size, in points — matches the existing 10.5pt default
   *  for most families; Counter-Ready runs larger for counter legibility
   *  (§4#2 "larger type, generous margins, high contrast"). */
  bodyPt: number;
  /** Page margin, in millimetres (top/bottom/left/right, uniform). */
  marginMm: number;
  /** Whether this family renders in monochrome (ATS-Plain only, §10's one
   *  deliberate exception) — informational; the ACTUAL colour-forcing
   *  happens in effectiveAccent above (acceptsAccent:false on the meta row),
   *  this flag just lets a renderer skip any accent-dependent flourish
   *  entirely rather than draw it in Graphite. */
  monochrome: boolean;
  /** A restrained header rule under the accent-heading row (Modern
   *  Professional's "a restrained accent rule," §4#7) — every other family
   *  renders the existing single hairline border only. */
  headerRule: boolean;
}

const TEMPLATE_TOKENS: Record<ResumeTemplateFamily, ResumeTemplateTokens> = {
  'ats-plain': { bodyPt: 10.5, marginMm: 16, monochrome: true, headerRule: false },
  'counter-ready': { bodyPt: 12, marginMm: 14, monochrome: false, headerRule: false },
  'everyday-clean': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'first-job-student': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'trades-certifications': { bodyPt: 11, marginMm: 15, monochrome: false, headerRule: false },
  'recent-graduate': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'modern-professional': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: true },
  'executive-compact': { bodyPt: 9.5, marginMm: 14, monochrome: false, headerRule: true },
  'classic-serif': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'selection-criteria': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'two-column-portfolio': { bodyPt: 10, marginMm: 14, monochrome: false, headerRule: false },
  'tech-impact': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: true },
  'corporate-consulting': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
  'elegant-header': { bodyPt: 10.5, marginMm: 16, monochrome: false, headerRule: false },
};

export function tokensForFamily(family: ResumeTemplateFamily): ResumeTemplateTokens {
  return TEMPLATE_TOKENS[family];
}
